/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useCallback, useEffect, useRef, useState} from 'react';

import {videoFactory} from '@amblique/base/app/plugins/video/video-factory';

/**
 * @typedef {import('@amblique/base/app/plugins/video/providers/abstract-video').AbstractVideo} AbstractVideo
 */

/**
 * React hook for video player management
 *
 * Handles video player initialization, lifecycle, and state management.
 * Uses video models (Vimeo, YouTube) from plugins/video/models.
 *
 * @param {string} videoId - Video ID or URL
 * @param {Object} options - Video options
 * @returns {{video: AbstractVideo, containerRef: React.RefObject<HTMLElement>, init: () => Promise<AbstractVideo>, destroy: () => void}} Player state and API
 *
 * @example Basic usage
 * const { player, metadata, isLoading, error } = useVideoPlayer('16401445', options);
 *
 * @example With lazy loading
 * const { player, init, isInitialized } = useVideoPlayer('16401445', { lazy: true });
 * // Later: await init();
 */
export function useVideoPlayer(videoId, options = {}) {
  const [video, setVideo] = useState(null);
  const [initVideoId] = useState(videoId);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);

  /**
   * Initialize video player
   */
  const init = useCallback(async () => {
    if (isLoading || !initVideoId) {
      return null;
    }

    setIsLoading(true);

    const newVideo = await videoFactory.createPlayer(initVideoId, containerRef.current, options);

    setVideo(newVideo);

    return newVideo;
  }, [initVideoId, containerRef, options, isLoading]);

  /**
   * Destroy video player
   */
  const destroy = useCallback(() => {
    if (video && !video.isDestroyed()) {
      video.destroy();
    }

    setIsLoading(false);
    setVideo(null);
  }, [video]);

  /**
   * Initialize player on mount (unless lazy)
   */
  useEffect(() => {
    if (!options.lazy && videoId && !video?.initialized) {
      init();
    } else if (videoId && video?.initialized && !video?.isDestroyed()) {
      video?.loadVideo(videoId);
    }
  }, [videoId, video]);

  /**
   * Update options when they change
   */
  useEffect(() => {
    if (video?.initialized && !video?.isDestroyed()) {
      video.setOptions(options);
    }
  }, [video, options]);

  return {containerRef, video, init, destroy};
}
